import z from "zod";
import { ZodSchema } from "@validation/utils/schemaUtils";
import { intl } from "LocaleWrapper";
import { paymentTermSelectOptionSchema } from "view/paymentterms/utils/paymentTerm.schema";
import { relationSelectOptionSchema } from "view/relations/utils/relation.schema";
import { vatCodeSelectOptionSchema } from "view/vatcodes/utils/vatCode.schema";

export const invoiceEditSchema = z.object({
	id: z.number().nullable(),
	invoiceNumber: z.string().max(100),
	debtor: relationSelectOptionSchema.nullable(),
	creditor: relationSelectOptionSchema.nullable(),
	paymentTerm: ZodSchema.requiredField(paymentTermSelectOptionSchema),
	date: ZodSchema.requiredField(z.number()),
});

export type InvoiceEditSchemaInput = z.input<typeof invoiceEditSchema>;
export type InvoiceEditSchemaOutput = z.output<typeof invoiceEditSchema>;

export const invoiceCreateSchema = z.object({
	invoiceNumber: z.string().max(100),
	creditor: ZodSchema.requiredField(relationSelectOptionSchema.nullable()),
	debtor: ZodSchema.requiredField(relationSelectOptionSchema.nullable()),
	date: ZodSchema.requiredField(z.number().nullable()),
	paymentTerm: ZodSchema.requiredField(paymentTermSelectOptionSchema.nullable()),
	lines: z.array(z.object({
		creditor: relationSelectOptionSchema.nullish(),
		debtor: relationSelectOptionSchema.nullish(),
	})).superRefine((val, ctx) => {
		// invoice cannot be created without at least one invoice line
		if (val.length === 0) {
			ctx.addIssue({ code: "custom", message: intl.formatMessage({ id: "NO_LINES_FOUND" }) });
			return;
		}

		// all lines on the invoice should have the same creditor and debtor
		const creditorIds = new Set(val.flatMap(l => l.creditor ? [ l.creditor.id ] : []));
		const debtorIds = new Set(val.flatMap(l => l.debtor ? [ l.debtor.id ] : []));

		if (creditorIds.size > 1 || debtorIds.size > 1) {
			ctx.addIssue({ code: "custom", message: intl.formatMessage({ id: "MIXED_CREDITOR_DEBTOR" }) });
		}
	}),
});

export type InvoiceCreateSchemaInput = z.input<typeof invoiceCreateSchema>;
export type InvoiceCreateSchemaOutput = z.output<typeof invoiceCreateSchema>;

export const advancePaymentSchema = z.object({
	amount: ZodSchema.requiredField(z.number().refine(value => value !== 0, intl.formatMessage({ id: "AMOUNT_CANNOT_BE_ZERO" }))),
	vatCode: ZodSchema.requiredField(vatCodeSelectOptionSchema),
});

export type AdvancePaymentSchemaInput = z.input<typeof advancePaymentSchema>;
export type AdvancePaymentSchemaOutput = z.output<typeof advancePaymentSchema>;
